import React, { useState } from 'react';
import { usePolicy } from '../context/PolicyContext';
import './Form.css';

const ReinstatePolicy: React.FC = () => {
  const { getPolicy, reinstatePolicy } = usePolicy();
  const [policyNumber, setPolicyNumber] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [policyFound, setPolicyFound] = useState(false);
  const [policyDetails, setPolicyDetails] = useState<any>(null);

  const handlePolicyCheck = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setPolicyFound(false);
    setPolicyDetails(null);

    if (!policyNumber.trim()) {
      setErrors({ policyNumber: 'Please enter a policy number' });
      return;
    }

    const policy = getPolicy(policyNumber.trim());
    if (policy) {
      if (policy.status !== 'cancelled') {
        setErrors({ policyNumber: 'This policy is not cancelled. Only cancelled policies can be reinstated.' });
        return;
      }
      setPolicyFound(true);
      setPolicyDetails(policy);
    } else {
      setErrors({ policyNumber: 'Policy not found' });
    }
  };

  const handleReinstate = (e: React.FormEvent) => {
    e.preventDefault();
    reinstatePolicy(policyNumber.trim());
    setSuccess(true);
    setPolicyNumber('');
    setPolicyFound(false);
    setPolicyDetails(null);

    setTimeout(() => setSuccess(false), 5000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="form-container">
      <h2>Reinstate Insurance Policy</h2>

      {success && (
        <div className="success-message">
          Policy has been reinstated successfully!
        </div>
      )}

      {!policyFound ? (
        <form onSubmit={handlePolicyCheck} className="form">
          <div className="form-group">
            <label htmlFor="policyNumber">Policy Number *</label>
            <input
              type="text"
              id="policyNumber"
              name="policyNumber"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              placeholder="Enter cancelled policy number"
              className={errors.policyNumber ? 'error' : ''}
            />
            {errors.policyNumber && <span className="error-text">{errors.policyNumber}</span>}
          </div>

          <button type="submit" className="submit-button">Verify Policy</button>
        </form>
      ) : (
        <div>
          {policyDetails && (
            <div className="policy-preview">
              <h3>Policy Information</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <strong>Policy Number:</strong>
                  <span>{policyDetails.policyNumber}</span>
                </div>
                <div className="detail-item">
                  <strong>Policy Holder:</strong>
                  <span>{policyDetails.policyHolderName}</span>
                </div>
                <div className="detail-item">
                  <strong>Policy Type:</strong>
                  <span>{policyDetails.policyType}</span>
                </div>
                <div className="detail-item">
                  <strong>Cancellation Date:</strong>
                  <span>{formatDate(policyDetails.cancellationDate!)}</span>
                </div>
                <div className="detail-item">
                  <strong>Cancellation Reason:</strong>
                  <span>{policyDetails.cancellationReason}</span>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleReinstate} className="form">
            <div className="form-group">
              <label htmlFor="policyNumber">Policy Number</label>
              <input
                type="text"
                id="policyNumber"
                name="policyNumber"
                value={policyNumber}
                disabled
                className="disabled-input"
              />
            </div>

            <div className="info-box">
              <p>
                <strong>Note:</strong> Reinstating this policy will restore it to active status.
                The policy will remain active until its original end date.
              </p>
            </div>

            <div className="button-group">
              <button type="submit" className="submit-button">Confirm Reinstatement</button>
              <button
                type="button"
                className="submit-button secondary"
                onClick={() => {
                  setPolicyFound(false);
                  setPolicyNumber('');
                  setPolicyDetails(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ReinstatePolicy;


